import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client';

import {
  CollectionEntriesDocument,
  getTypeOrNull,
  useCollectionEntriesLazyQuery,
} from '@spotify/confidence-graphql';

import { makeResourceEntryFilter } from '../utils';
import { useFavoritesCollection } from './useFavoritesCollection';

const getCachedCollectionEntries = (
  client: ReturnType<typeof useApolloClient>,
  collectionName: string,
  resourceName: string,
) => {
  try {
    const data = client.readQuery({
      query: CollectionEntriesDocument,
      variables: {
        parent: collectionName,
        filter: makeResourceEntryFilter(resourceName),
        pageSize: 1,
      },
    });
    return getTypeOrNull(data?.collectionEntries)?.collectionEntries ?? [];
  } catch {
    return [];
  }
};

export const useEnsureFavorite = () => {
  const { addToFavorites, favoritesCollectionName } = useFavoritesCollection();
  const client = useApolloClient();
  const [fetchEntries] = useCollectionEntriesLazyQuery({
    fetchPolicy: 'network-only',
  });

  const ensureFavorite = useCallback(
    async (resourceName: string): Promise<void> => {
      if (!favoritesCollectionName) return;
      if (
        getCachedCollectionEntries(
          client,
          favoritesCollectionName,
          resourceName,
        ).length > 0
      ) {
        return;
      }
      try {
        const { data } = await fetchEntries({
          variables: {
            parent: favoritesCollectionName,
            filter: makeResourceEntryFilter(resourceName),
            pageSize: 1,
          },
        });
        const entries = getTypeOrNull(
          data?.collectionEntries,
        )?.collectionEntries;
        if (entries?.length) return;
        await addToFavorites(resourceName);
      } catch {
        return;
      }
    },
    [favoritesCollectionName, fetchEntries, addToFavorites, client],
  );

  return { ensureFavorite };
};
